/**
 * Dynamic Market Sentiment Engine (StarX Options Core)
 * 
 * Provides continuous, realistic, organic market sentiment dynamics:
 * - Real-time tick reactivity (bullish/bearish tick momentum nudges sentiment)
 * - Order flow micro-fluctuations (mimics global community trades entering the pool)
 * - Mean reversion to baseline asset equilibrium (bounded within realistic 22% - 88% range)
 * - Smooth lerp interpolation for fluid visual movement
 */

export interface SentimentState {
  symbol: string;
  call: number; // Integer percentage (e.g. 74)
  put: number;  // Integer percentage (e.g. 26)
  rawCall: number; // Float for sub-pixel calculations
}

export type SentimentListener = (state: SentimentState) => void;

export class SentimentEngine {
  private static instance: SentimentEngine | null = null;

  private currentSymbol: string = 'EURUSD_OTC';
  private baselineCall: number = 68;
  private currentCall: number = 68;
  private targetCall: number = 68;
  private lastPrice: number | null = null;
  private momentum: number = 0;

  private minBound: number = 22;
  private maxBound: number = 88;

  private heartbeatTimer: any = null;
  private animationTimer: any = null;
  private listeners: Set<SentimentListener> = new Set();
  private lastEmittedCall: number = -1;

  public static getShared(): SentimentEngine {
    if (!this.instance) {
      this.instance = new SentimentEngine();
      this.instance.start();
    }
    return this.instance;
  }

  constructor() {
    this.initSymbolBaseline(this.currentSymbol);
  }

  public subscribe(listener: SentimentListener): () => void {
    this.listeners.add(listener);
    // Immediately emit current state
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  public getState(): SentimentState {
    const call = Math.max(this.minBound, Math.min(this.maxBound, Math.round(this.currentCall)));
    const put = 100 - call;
    return {
      symbol: this.currentSymbol,
      call,
      put,
      rawCall: this.currentCall,
    };
  }

  public setSymbol(symbol: string, initialPrice?: number): void {
    if (!symbol) return;
    this.currentSymbol = symbol;
    this.lastPrice = initialPrice && initialPrice > 0 ? initialPrice : null;
    this.momentum = 0;
    this.initSymbolBaseline(symbol);
    this.notify();
  }

  public recordTick(price: number): void {
    if (!price || price <= 0) return;
    if (this.lastPrice !== null && this.lastPrice > 0) {
      const delta = price - this.lastPrice;
      if (delta !== 0) {
        // Price tick momentum:
        // An uptick pulls call sentiment up (+0.6% to +1.8%)
        // A downtick pulls call sentiment down (-0.6% to -1.8%)
        const direction = delta > 0 ? 1 : -1;
        const magnitude = Math.min(1.8, Math.max(0.6, Math.abs(delta) / (price * 0.0001)));
        const shift = direction * magnitude + (Math.random() - 0.5) * 0.4;

        this.momentum = this.momentum * 0.7 + direction * 0.8;
        this.targetCall = this.clamp(this.targetCall + shift);
      }
    }
    this.lastPrice = price;
  }

  public recordOrder(direction: 'CALL' | 'PUT'): void {
    // When a trader places an order, immediate responsive nudge (+1.2% to +2.4%)
    const shift = direction === 'CALL'
      ? (1.2 + Math.random() * 1.2)
      : -(1.2 + Math.random() * 1.2);

    this.targetCall = this.clamp(this.targetCall + shift);
    this.momentum += (direction === 'CALL' ? 1.0 : -1.0);
  }

  public start(): void {
    if (typeof window === 'undefined') return;
    this.stop();

    // 1. Organic Order Flow Heartbeat (runs every 1.4s - 2.2s)
    const scheduleNextHeartbeat = () => {
      const delay = 1400 + Math.random() * 800;
      this.heartbeatTimer = setTimeout(() => {
        this.stepOrganicFlow();
        scheduleNextHeartbeat();
      }, delay);
    };
    scheduleNextHeartbeat();

    // 2. Smooth Interpolation Loop (every 50ms)
    this.animationTimer = setInterval(() => {
      this.stepInterpolation();
    }, 50);
  }

  public stop(): void {
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.animationTimer) {
      clearInterval(this.animationTimer);
      this.animationTimer = null;
    }
  }

  private initSymbolBaseline(symbol: string): void {
    // Deterministic organic baseline based on asset string hash
    let hash = 0;
    for (let i = 0; i < symbol.length; i++) {
      hash = (hash * 31 + symbol.charCodeAt(i)) % 1000;
    }
    // Baseline typically hovers in retail sweet spot (56% to 76% Call)
    this.baselineCall = 56 + (hash % 21);
    this.currentCall = this.baselineCall;
    this.targetCall = this.baselineCall;
  }

  private stepOrganicFlow(): void {
    // Mean reversion to anchor baseline:
    const meanReversion = (this.baselineCall - this.targetCall) * 0.08;

    // Organic community trade drift (-1.4% to +1.4%):
    const tradeNoise = (Math.random() - 0.48) * 2.2;

    // Momentum decay:
    this.momentum *= 0.82;

    this.targetCall = this.clamp(this.targetCall + meanReversion + tradeNoise + this.momentum * 0.4);
  }

  private stepInterpolation(): void {
    const diff = this.targetCall - this.currentCall;
    if (Math.abs(diff) < 0.02) {
      this.currentCall = this.targetCall;
    } else {
      // Fluid easing towards target (step ~12% per frame)
      this.currentCall += diff * 0.12;
    }

    const currentRounded = Math.round(this.currentCall);
    if (currentRounded !== this.lastEmittedCall) {
      this.lastEmittedCall = currentRounded;
      this.notify();
    }
  }

  private clamp(val: number): number {
    return Math.max(this.minBound, Math.min(this.maxBound, val));
  }

  private notify(): void {
    const state = this.getState();
    this.listeners.forEach((fn) => {
      try {
        fn(state);
      } catch (err) {
        console.error('[SentimentEngine] Listener error:', err);
      }
    });
  }
}
